import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  Bell,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Eye,
  Filter,
  Inbox,
  Loader2,
  Megaphone,
  RefreshCw,
  Search,
  Send,
  ShieldCheck,
  Smartphone,
  UserCheck,
  Users,
  X,
  XCircle,
} from "lucide-react";
import { AdminNotification, NotificationCampaign, NotificationSendSummary, PushStatus } from "../../types/admin";
import { PaginationMeta } from "../../types/api";
import { getCurrentTokenAccessInfo } from "../../utils/jwt";
import { fetchDashboard } from "../dashboard/dashboardApi";
import {
  fetchNotificationCampaigns,
  fetchNotificationDetail,
  fetchNotifications,
  sendNotification,
  sendSingleNotification,
} from "./notificationApi";
import {
  NotificationUserOption,
  readNotificationUserCache,
  refreshNotificationUserCache,
} from "./notificationUserCache";
import styles from "./NotificationPage.module.css";

type Tab = "history" | "single" | "bulk" | "campaigns";
type Toast = { kind: "success" | "error" | "info"; message: string } | null;
type SendMode = "single" | "bulk";

const PAGE_SIZE = 20;
const dateFormatter = new Intl.DateTimeFormat("tr-TR", { dateStyle: "medium", timeStyle: "short" });

const statusLabels: Record<PushStatus, string> = {
  pending: "Bekliyor",
  sent: "Gönderildi",
  failed: "Başarısız",
  skipped: "Atlandı",
};

function formatDate(value?: string | null) {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : dateFormatter.format(date);
}

function parseDataPairs(input: string): Record<string, string> | undefined {
  const result: Record<string, string> = {};
  input.split("\n").map((line) => line.trim()).filter(Boolean).forEach((line) => {
    const separator = line.indexOf("=");
    if (separator < 1) throw new Error(`Geçersiz data satırı: "${line}". key=value biçimini kullanın.`);
    const key = line.slice(0, separator).trim();
    const value = line.slice(separator + 1).trim();
    if (!key) throw new Error("Data anahtarı boş olamaz.");
    result[key] = value;
  });
  return Object.keys(result).length ? result : undefined;
}

function parseUserIds(input: string) {
  return Array.from(new Set(input.split(/[\n,]/).map((id) => id.trim()).filter(Boolean)));
}

function userLabel(user: NotificationUserOption) {
  return user.name || user.email || user.user_id;
}

function normalizeSearch(value: string) {
  return value.trim().toLocaleLowerCase("tr-TR");
}

function Summary({ value }: { value: NotificationSendSummary }) {
  return (
    <>
      {value.push_status && (
        <div className={`${styles.deliveryState} ${styles[`delivery_${value.push_status}`]}`}>
          <strong>Push: {value.push_status}</strong>
          <span>{value.reason || (value.push_status === "sent" ? "Bildirim push sağlayıcısına teslim edildi." : "Push teslimatı tamamlanmadı.")}</span>
        </div>
      )}
      <div className={styles.summaryGrid}>
        {[
          ["İstenen kullanıcı", value.requested_users],
          ["Hedef cihaz", value.targeted_devices],
          ["Gönderildi", value.sent],
          ["Başarısız", value.failed],
          ["Atlandı", value.skipped],
        ].map(([label, count]) => (
          <div key={String(label)}><strong>{count}</strong><span>{label}</span></div>
        ))}
      </div>
    </>
  );
}

export default function NotificationPage() {
  const access = getCurrentTokenAccessInfo();
  const canWrite = Boolean(access?.hasPanelAccess);
  const [tab, setTab] = useState<Tab>("history");
  const [toast, setToast] = useState<Toast>(null);
  const [stats, setStats] = useState({ total: 0, week: 0, failed: 0, devices: 0 });

  const [notifications, setNotifications] = useState<AdminNotification[]>([]);
  const [historyMeta, setHistoryMeta] = useState<PaginationMeta | null>(null);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyStatus, setHistoryStatus] = useState<"all" | PushStatus>("all");
  const [userInput, setUserInput] = useState("");
  const [userFilter, setUserFilter] = useState("");
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState("");
  const [detail, setDetail] = useState<AdminNotification | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [campaigns, setCampaigns] = useState<NotificationCampaign[]>([]);
  const [campaignMeta, setCampaignMeta] = useState<PaginationMeta | null>(null);
  const [campaignPage, setCampaignPage] = useState(1);
  const [campaignLoading, setCampaignLoading] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<NotificationCampaign | null>(null);

  const [single, setSingle] = useState({ userId: "", title: "", body: "", data: "" });
  const [userDirectory, setUserDirectory] = useState<NotificationUserOption[]>([]);
  const [userDirectoryFetchedAt, setUserDirectoryFetchedAt] = useState(0);
  const [userDirectoryInitialized, setUserDirectoryInitialized] = useState(false);
  const [userDirectoryLoading, setUserDirectoryLoading] = useState(false);
  const [userDirectoryError, setUserDirectoryError] = useState("");
  const [userQuery, setUserQuery] = useState("");
  const [userPickerOpen, setUserPickerOpen] = useState(false);
  const [bulk, setBulk] = useState<{
    audience: "all" | "users";
    userIds: string;
    title: string;
    body: string;
    data: string;
  }>({ audience: "all", userIds: "", title: "", body: "", data: "" });
  const [confirmMode, setConfirmMode] = useState<SendMode | null>(null);
  const [sending, setSending] = useState(false);
  const [lastSummary, setLastSummary] = useState<NotificationSendSummary | null>(null);

  const loadStats = useCallback(() => {
    fetchDashboard().then((data) => setStats({
      total: data.notifications.total_sent,
      week: data.notifications.sent_last_7_days,
      failed: data.notifications.failed,
      devices: data.devices.notification_enabled,
    })).catch(() => undefined);
  }, []);

  const loadHistory = useCallback(async (quiet = false) => {
    try {
      if (!quiet) setHistoryLoading(true);
      setHistoryError("");
      const response = await fetchNotifications({
        page: historyPage,
        limit: PAGE_SIZE,
        user_id: userFilter || undefined,
        push_status: historyStatus === "all" ? undefined : historyStatus,
      });
      setNotifications(response.items);
      setHistoryMeta(response.meta);
    } catch (error: any) {
      setHistoryError(error?.message || "Bildirim geçmişi alınamadı.");
    } finally {
      setHistoryLoading(false);
    }
  }, [historyPage, historyStatus, userFilter]);

  const loadCampaigns = useCallback(async () => {
    try {
      setCampaignLoading(true);
      const response = await fetchNotificationCampaigns(campaignPage, PAGE_SIZE);
      setCampaigns(response.items);
      setCampaignMeta(response.meta);
    } catch (error: any) {
      setToast({ kind: "error", message: error?.message || "Kampanyalar alınamadı." });
    } finally {
      setCampaignLoading(false);
    }
  }, [campaignPage]);

  const loadUserDirectory = useCallback(async (force = false) => {
    const cached = readNotificationUserCache(true);
    if (cached) {
      setUserDirectory(cached.users);
      setUserDirectoryFetchedAt(cached.fetchedAt);
      if (!force && !cached.isStale) return;
    }

    try {
      setUserDirectoryLoading(true);
      setUserDirectoryError("");
      const refreshed = await refreshNotificationUserCache();
      setUserDirectory(refreshed.users);
      setUserDirectoryFetchedAt(refreshed.fetchedAt);
    } catch (error: any) {
      setUserDirectoryError(
        cached
          ? "Güncel liste alınamadı; kayıtlı yerel liste gösteriliyor."
          : error?.message || "Kullanıcı listesi alınamadı."
      );
    } finally {
      setUserDirectoryLoading(false);
    }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { if (tab === "history") loadHistory(); }, [tab, loadHistory]);
  useEffect(() => { if (tab === "campaigns") loadCampaigns(); }, [tab, loadCampaigns]);
  useEffect(() => {
    if (tab === "single" && !userDirectoryInitialized) {
      setUserDirectoryInitialized(true);
      loadUserDirectory();
    }
  }, [loadUserDirectory, tab, userDirectoryInitialized]);

  const historyPages = Number(historyMeta?.total_pages || 0);
  const campaignPages = Number(campaignMeta?.total_pages || 0);
  const bulkUsers = useMemo(() => parseUserIds(bulk.userIds), [bulk.userIds]);
  const selectedUser = useMemo(
    () => userDirectory.find((user) => user.user_id === single.userId),
    [single.userId, userDirectory]
  );
  const matchingUsers = useMemo(() => {
    const query = normalizeSearch(userQuery);
    const users = query
      ? userDirectory.filter((user) =>
          normalizeSearch(
            `${user.name} ${user.email} ${user.user_id}`
          ).includes(query)
        )
      : userDirectory;
    return users.slice(0, 40);
  }, [userDirectory, userQuery]);

  const openDetail = async (id: string) => {
    try {
      setDetailLoading(true);
      setDetail(await fetchNotificationDetail(id));
    } catch (error: any) {
      setToast({ kind: "error", message: error?.message || "Bildirim detayı alınamadı." });
    } finally {
      setDetailLoading(false);
    }
  };

  const requestSend = (mode: SendMode) => {
    setToast(null);
    try {
      const form = mode === "single" ? single : bulk;
      if (!form.title.trim() || !form.body.trim()) throw new Error("Başlık ve mesaj zorunludur.");
      if (form.title.trim().length > 140 || form.body.trim().length > 500) throw new Error("Başlık en fazla 140, mesaj en fazla 500 karakter olabilir.");
      parseDataPairs(form.data);
      if (mode === "single" && !single.userId.trim()) throw new Error("Kullanıcı ID zorunludur.");
      if (mode === "bulk" && bulk.audience === "users" && bulkUsers.length === 0) throw new Error("En az bir kullanıcı ID girin.");
      setConfirmMode(mode);
    } catch (error: any) {
      setToast({ kind: "error", message: error.message });
    }
  };

  const executeSend = async () => {
    if (!confirmMode) return;
    try {
      setSending(true);
      const summary = confirmMode === "single"
        ? await sendSingleNotification({
            user_id: single.userId.trim(),
            title: single.title.trim(),
            body: single.body.trim(),
            data: parseDataPairs(single.data),
          })
        : await sendNotification({
            audience: { type: bulk.audience, user_ids: bulk.audience === "users" ? bulkUsers : [] },
            title: bulk.title.trim(),
            body: bulk.body.trim(),
            data: parseDataPairs(bulk.data),
          });
      setLastSummary(summary);
      setConfirmMode(null);
      const explicitlyDelivered = summary.push_status === "sent";
      const explicitlyNotDelivered =
        Boolean(summary.push_status) && summary.push_status !== "sent";
      setToast({
        kind: explicitlyDelivered ? "success" : explicitlyNotDelivered ? "error" : "info",
        message: explicitlyDelivered
          ? `${summary.sent} bildirim gönderildi; ${summary.failed} başarısız, ${summary.skipped} atlandı.`
          : explicitlyNotDelivered
            ? `İstek işlendi ancak push gönderilmedi: ${summary.reason || summary.push_status}.`
            : "Bildirim isteği API tarafından kabul edildi; response içinde ayrı bir push teslimat durumu bulunmuyor.",
      });
      if (confirmMode === "single") setSingle((current) => ({ ...current, title: "", body: "", data: "" }));
      else setBulk((current) => ({ ...current, title: "", body: "", data: "" }));
      loadStats();
      if (tab === "history") loadHistory(true);
    } catch (error: any) {
      setToast({ kind: "error", message: error?.message || "Bildirim gönderilemedi." });
    } finally {
      setSending(false);
    }
  };

  return (
    <main className={styles.page}>
      <section className={styles.hero}>
        <div>
          <span className={styles.eyebrow}><Bell size={15} /> BİLDİRİM MERKEZİ</span>
          <h1>Doğru mesajı, doğru kitleye ulaştırın.</h1>
          <p>Tekil push gönderimlerini ve toplu kampanyaları yönetin; teslimat sonuçlarını ve tüm bildirim ayrıntılarını izleyin.</p>
        </div>
        <span className={styles.access}><ShieldCheck size={16} />{access?.roles.join(" · ") || "YETKİ YOK"}</span>
      </section>

      <section className={styles.stats}>
        <article><span><Send size={19} /></span><div><strong>{stats.total.toLocaleString("tr-TR")}</strong><small>Toplam gönderim</small></div></article>
        <article><span><Clock3 size={19} /></span><div><strong>{stats.week.toLocaleString("tr-TR")}</strong><small>Son 7 gün</small></div></article>
        <article><span><XCircle size={19} /></span><div><strong>{stats.failed.toLocaleString("tr-TR")}</strong><small>Başarısız</small></div></article>
        <article><span><Smartphone size={19} /></span><div><strong>{stats.devices.toLocaleString("tr-TR")}</strong><small>Bildirim açık cihaz</small></div></article>
      </section>

      {toast && <div className={`${styles.toast} ${styles[toast.kind]}`} role="alert">{toast.kind === "success" ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}<span>{toast.message}</span><button onClick={() => setToast(null)} aria-label="Mesajı kapat"><X size={16} /></button></div>}

      <nav className={styles.tabs} aria-label="Bildirim bölümleri">
        {([
          ["history", Inbox, "Geçmiş"],
          ["single", Send, "Tekil gönder"],
          ["bulk", Megaphone, "Toplu gönder"],
          ["campaigns", Users, "Kampanyalar"],
        ] as const).map(([key, Icon, label]) => (
          <button key={key} className={tab === key ? styles.activeTab : ""} onClick={() => setTab(key)}><Icon size={17} />{label}</button>
        ))}
      </nav>

      {tab === "history" && (
        <section className={styles.panel}>
          <header className={styles.panelHeader}>
            <div><h2>Bildirim geçmişi</h2><p>{historyMeta?.total ?? notifications.length} kayıt</p></div>
            <button className={styles.iconButton} onClick={() => loadHistory()} disabled={historyLoading} aria-label="Yenile"><RefreshCw size={17} className={historyLoading ? styles.spin : ""} /></button>
          </header>
          <form className={styles.filters} onSubmit={(event) => { event.preventDefault(); setHistoryPage(1); setUserFilter(userInput.trim()); }}>
            <label><span><Filter size={14} /> Push durumu</span><select value={historyStatus} onChange={(e) => { setHistoryPage(1); setHistoryStatus(e.target.value as typeof historyStatus); }}><option value="all">Tümü</option>{Object.entries(statusLabels).map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select></label>
            <label className={styles.grow}><span><Users size={14} /> Kullanıcı ID</span><input value={userInput} onChange={(e) => setUserInput(e.target.value)} placeholder="Tam kullanıcı ID ile filtrele" /></label>
            <button className={styles.primaryButton} type="submit"><Filter size={16} /> Uygula</button>
            {(userFilter || historyStatus !== "all") && <button className={styles.secondaryButton} type="button" onClick={() => { setUserInput(""); setUserFilter(""); setHistoryStatus("all"); setHistoryPage(1); }}>Temizle</button>}
          </form>

          {historyError ? <Empty icon={AlertCircle} title="Geçmiş yüklenemedi" text={historyError} /> : historyLoading ? <Loading /> : notifications.length === 0 ? <Empty icon={Inbox} title="Kayıt bulunamadı" text="Filtreleri değiştirerek yeniden deneyin." /> : (
            <>
              <div className={styles.tableWrap}>
                <table>
                  <thead><tr><th>Bildirim</th><th>Kullanıcı</th><th>Tür / Aksiyon</th><th>Durum</th><th>Tarih</th><th aria-label="İşlem" /></tr></thead>
                  <tbody>{notifications.map((item) => <tr key={item.id}>
                    <td><strong>{item.title}</strong><small>{item.body}</small></td>
                    <td><code title={item.user_id}>{item.user_id || "—"}</code></td>
                    <td><span>{item.type}</span><small>{item.action}</small></td>
                    <td><Status value={item.push_status} /><small>{item.is_read ? "Okundu" : "Okunmadı"}</small></td>
                    <td>{formatDate(item.created_at)}</td>
                    <td><button className={styles.rowButton} onClick={() => openDetail(item.id)} disabled={detailLoading}><Eye size={16} /> Detay</button></td>
                  </tr>)}</tbody>
                </table>
              </div>
              <div className={styles.mobileList}>{notifications.map((item) => <article key={item.id}>
                <header><Status value={item.push_status} /><small>{formatDate(item.created_at)}</small></header>
                <h3>{item.title}</h3><p>{item.body}</p><code>{item.user_id || "Kullanıcı yok"}</code>
                <button className={styles.rowButton} onClick={() => openDetail(item.id)}><Eye size={16} /> Tüm ayrıntılar</button>
              </article>)}</div>
              <Pagination page={historyPage} pages={historyPages} count={notifications.length} meta={historyMeta} onChange={setHistoryPage} />
            </>
          )}
        </section>
      )}

      {tab === "single" && <Composer title="Tekil bildirim gönder" description="Kullanıcıyı ad veya e-posta ile seçin; bildirim aktif push cihazlarına gönderilir." icon={Send}>
        <section className={styles.userPicker}>
          <header className={styles.userPickerHeader}>
            <div>
              <strong>Kullanıcı seçin *</strong>
              <small>
                {userDirectory.length
                  ? `${userDirectory.length} kullanıcı yerel listede`
                  : "Kullanıcı dizini hazırlanıyor"}
              </small>
            </div>
            <button
              type="button"
              className={styles.cacheRefresh}
              onClick={() => loadUserDirectory(true)}
              disabled={userDirectoryLoading}
            >
              <RefreshCw size={15} className={userDirectoryLoading ? styles.spin : ""} />
              Listeyi yenile
            </button>
          </header>

          {selectedUser && (
            <div className={styles.selectedUser}>
              <span><UserCheck size={19} /></span>
              <div>
                <strong>{userLabel(selectedUser)}</strong>
                <small>{selectedUser.email || "E-posta yok"} · {selectedUser.status}</small>
                <code>{selectedUser.user_id}</code>
              </div>
              <button
                type="button"
                onClick={() => {
                  setSingle((current) => ({ ...current, userId: "" }));
                  setUserQuery("");
                  setUserPickerOpen(true);
                }}
                aria-label="Kullanıcı seçimini kaldır"
              >
                <X size={16} />
              </button>
            </div>
          )}

          <div className={styles.userSearch}>
            <Search size={17} />
            <input
              value={userQuery}
              onFocus={() => setUserPickerOpen(true)}
              onChange={(event) => {
                setUserQuery(event.target.value);
                setUserPickerOpen(true);
              }}
              placeholder="İsim, e-posta veya UID ile ara"
              aria-label="Bildirim kullanıcısı ara"
              autoComplete="off"
            />
            {userDirectoryLoading && <Loader2 size={17} className={styles.spin} />}
          </div>

          {userPickerOpen && (
            <div className={styles.userResults}>
              {matchingUsers.length ? matchingUsers.map((user) => (
                <button
                  type="button"
                  key={user.user_id}
                  className={user.user_id === single.userId ? styles.activeUserResult : ""}
                  onClick={() => {
                    setSingle((current) => ({ ...current, userId: user.user_id }));
                    setUserQuery(userLabel(user));
                    setUserPickerOpen(false);
                  }}
                >
                  <span>{(userLabel(user).charAt(0) || "?").toLocaleUpperCase("tr-TR")}</span>
                  <div>
                    <strong>{userLabel(user)}</strong>
                    <small>{user.email || user.user_id}</small>
                  </div>
                  <em>{user.status}</em>
                </button>
              )) : (
                <div className={styles.noUserResult}>
                  {userDirectoryLoading ? "Kullanıcılar yükleniyor…" : "Bu aramayla eşleşen kullanıcı bulunamadı."}
                </div>
              )}
            </div>
          )}

          <footer className={styles.cacheInfo}>
            <span>
              {userDirectoryFetchedAt
                ? `Yerel liste: ${formatDate(new Date(userDirectoryFetchedAt).toISOString())}`
                : "Henüz yerel kullanıcı listesi yok"}
            </span>
            {userDirectoryError && <strong>{userDirectoryError}</strong>}
          </footer>

          <details className={styles.manualUid}>
            <summary>Kullanıcı listede yoksa UID ile manuel giriş</summary>
            <label>
              <span>Kullanıcı UID</span>
              <input
                value={single.userId}
                maxLength={200}
                onChange={(event) =>
                  setSingle((current) => ({ ...current, userId: event.target.value }))
                }
                placeholder="Firebase kullanıcı UID"
              />
            </label>
          </details>
        </section>
        <MessageFields value={single} onChange={(patch) => setSingle({ ...single, ...patch })} />
        <button className={styles.primaryButton} disabled={!canWrite} onClick={() => requestSend("single")}><Send size={17} /> Gönderimi incele</button>
      </Composer>}

      {tab === "bulk" && <Composer title="Toplu kampanya gönder" description="Tüm kullanıcılara veya seçtiğiniz kullanıcı listesine bir push kampanyası oluşturur." icon={Megaphone}>
        <div className={styles.audience}>
          <button className={bulk.audience === "all" ? styles.selectedAudience : ""} onClick={() => setBulk({ ...bulk, audience: "all" })}><Megaphone size={20} /><strong>Tüm kullanıcılar</strong><small>Tüm uygun cihazlar</small></button>
          <button className={bulk.audience === "users" ? styles.selectedAudience : ""} onClick={() => setBulk({ ...bulk, audience: "users" })}><Users size={20} /><strong>Seçili kullanıcılar</strong><small>UID listesiyle hedefle</small></button>
        </div>
        {bulk.audience === "users" && <label><span>Kullanıcı ID’leri * <em>{bulkUsers.length} benzersiz kullanıcı</em></span><textarea rows={4} value={bulk.userIds} onChange={(e) => setBulk({ ...bulk, userIds: e.target.value })} placeholder={"UID-1\nUID-2\nUID-3"} /></label>}
        <MessageFields value={bulk} onChange={(patch) => setBulk({ ...bulk, ...patch })} />
        <button className={styles.primaryButton} disabled={!canWrite} onClick={() => requestSend("bulk")}><Megaphone size={17} /> Kampanyayı incele</button>
      </Composer>}

      {tab === "campaigns" && (
        <section className={styles.panel}>
          <header className={styles.panelHeader}><div><h2>Kampanya geçmişi</h2><p>{campaignMeta?.total ?? campaigns.length} kampanya</p></div><button className={styles.iconButton} onClick={loadCampaigns} aria-label="Yenile"><RefreshCw size={17} className={campaignLoading ? styles.spin : ""} /></button></header>
          {campaignLoading ? <Loading /> : campaigns.length === 0 ? <Empty icon={Megaphone} title="Kampanya bulunamadı" text="İlk toplu gönderiminiz burada görünecek." /> : <>
            <div className={styles.campaignGrid}>{campaigns.map((campaign) => <button key={campaign.campaign_id} className={styles.campaignCard} onClick={() => setSelectedCampaign(campaign)}>
              <header><span><Megaphone size={17} /></span><small>{formatDate(campaign.created_at)}</small></header>
              <h3>{campaign.title}</h3><p>{campaign.body}</p>
              <div><span><strong>{campaign.sent}</strong> gönderildi</span><span><strong>{campaign.failed}</strong> başarısız</span><span><strong>{campaign.skipped}</strong> atlandı</span></div>
              <footer><Users size={14} />{campaign.audience.type === "all" ? "Tüm kullanıcılar" : `${campaign.audience.user_ids.length} seçili kullanıcı`}<Eye size={15} /></footer>
            </button>)}</div>
            <Pagination page={campaignPage} pages={campaignPages} count={campaigns.length} meta={campaignMeta} onChange={setCampaignPage} />
          </>}
        </section>
      )}

      {lastSummary && <section className={styles.result}><header><CheckCircle2 size={20} /><div><strong>Son gönderim sonucu</strong><small>API teslimat özeti</small></div><button onClick={() => setLastSummary(null)} aria-label="Sonucu kapat"><X size={16} /></button></header><Summary value={lastSummary} /></section>}

      {(detail || detailLoading) && <div className={styles.overlay} role="presentation" onMouseDown={() => !detailLoading && setDetail(null)}><aside className={styles.drawer} role="dialog" aria-modal="true" onMouseDown={(e) => e.stopPropagation()}>
        <header><div><span><Bell size={19} /></span><div><h2>Bildirim detayı</h2><p>API kaydındaki tüm alanlar</p></div></div><button onClick={() => setDetail(null)} aria-label="Kapat"><X size={19} /></button></header>
        {detailLoading && !detail ? <Loading /> : detail && <NotificationDetail item={detail} />}
      </aside></div>}

      {selectedCampaign && <div className={styles.overlay} role="presentation" onMouseDown={() => setSelectedCampaign(null)}><aside className={styles.drawer} role="dialog" aria-modal="true" onMouseDown={(e) => e.stopPropagation()}>
        <header><div><span><Megaphone size={19} /></span><div><h2>Kampanya detayı</h2><p>Gönderim ve hedef kitle özeti</p></div></div><button onClick={() => setSelectedCampaign(null)} aria-label="Kapat"><X size={19} /></button></header>
        <CampaignDetail item={selectedCampaign} />
      </aside></div>}

      {confirmMode && <div className={styles.overlay} role="presentation" onMouseDown={() => !sending && setConfirmMode(null)}><section className={styles.confirm} role="dialog" aria-modal="true" onMouseDown={(e) => e.stopPropagation()}>
        <span className={styles.confirmIcon}>{confirmMode === "bulk" ? <Megaphone /> : <Send />}</span>
        <h2>Gönderimi onaylayın</h2>
        <p>{confirmMode === "bulk" && bulk.audience === "all" ? "Bu kampanya bildirim alabilen tüm kullanıcılara gönderilecek." : confirmMode === "bulk" ? `${bulkUsers.length} seçili kullanıcı hedeflenecek.` : `${selectedUser ? userLabel(selectedUser) : single.userId} kullanıcısının aktif cihazları hedeflenecek.`}</p>
        <div className={styles.preview}><strong>{confirmMode === "single" ? single.title : bulk.title}</strong><span>{confirmMode === "single" ? single.body : bulk.body}</span></div>
        <footer><button className={styles.secondaryButton} onClick={() => setConfirmMode(null)} disabled={sending}>Vazgeç</button><button className={styles.primaryButton} onClick={executeSend} disabled={sending}>{sending ? <Loader2 className={styles.spin} size={17} /> : <Send size={17} />}{sending ? "Gönderiliyor…" : "Şimdi gönder"}</button></footer>
      </section></div>}
    </main>
  );
}

function MessageFields({ value, onChange }: { value: { title: string; body: string; data: string }; onChange: (patch: Partial<{ title: string; body: string; data: string }>) => void }) {
  return <><label><span>Başlık * <em>{value.title.length}/140</em></span><input value={value.title} maxLength={140} onChange={(e) => onChange({ title: e.target.value })} placeholder="Yeni içerik hazır" /></label><label><span>Mesaj * <em>{value.body.length}/500</em></span><textarea rows={5} value={value.body} maxLength={500} onChange={(e) => onChange({ body: e.target.value })} placeholder="Kullanıcıların göreceği bildirim metni…" /></label><label><span>Ek data <em>opsiyonel · key=value</em></span><textarea rows={4} value={value.data} onChange={(e) => onChange({ data: e.target.value })} placeholder={"action=OPEN_SONG\nsongv2_id=940d8907-..."} /></label></>;
}

function Composer({ title, description, icon: Icon, children }: { title: string; description: string; icon: React.ElementType; children: React.ReactNode }) {
  return <section className={styles.composer}><header><span><Icon size={21} /></span><div><h2>{title}</h2><p>{description}</p></div></header><div className={styles.form}>{children}</div></section>;
}

function Status({ value }: { value: PushStatus }) {
  return <span className={`${styles.status} ${styles[value]}`}>{value === "sent" ? <CheckCircle2 /> : value === "failed" ? <XCircle /> : value === "pending" ? <Clock3 /> : <AlertCircle />}{statusLabels[value] || value}</span>;
}

function Loading() { return <div className={styles.loading}><Loader2 className={styles.spin} /><span>Veriler yükleniyor…</span></div>; }
function Empty({ icon: Icon, title, text }: { icon: React.ElementType; title: string; text: string }) { return <div className={styles.empty}><Icon /><strong>{title}</strong><span>{text}</span></div>; }

function Pagination({ page, pages, count, meta, onChange }: { page: number; pages: number; count: number; meta: PaginationMeta | null; onChange: (page: number) => void }) {
  const hasNext = pages ? page < pages : count >= PAGE_SIZE;
  return <footer className={styles.pagination}><span>Sayfa {page}{pages ? ` / ${pages}` : ""}{meta?.total != null ? ` · ${meta.total} kayıt` : ""}</span><div><button disabled={page <= 1} onClick={() => onChange(page - 1)}><ChevronLeft size={17} /> Önceki</button><button disabled={!hasNext} onClick={() => onChange(page + 1)}>Sonraki <ChevronRight size={17} /></button></div></footer>;
}

interface DetailRow {
  label: string;
  value: React.ReactNode;
}

function DetailRows({ rows }: { rows: DetailRow[] }) {
  return <dl className={styles.detailRows}>{rows.map(({ label, value }) => <div key={label}><dt>{label}</dt><dd>{value || "—"}</dd></div>)}</dl>;
}

function NotificationDetail({ item }: { item: AdminNotification }) {
  return <div className={styles.drawerBody}><div className={styles.messagePreview}><Bell size={20} /><div><strong>{item.title}</strong><p>{item.body}</p></div></div><Status value={item.push_status} /><DetailRows rows={[
    { label: "Bildirim ID", value: <code>{item.id}</code> }, { label: "Kullanıcı ID", value: <code>{item.user_id}</code> }, { label: "Tür", value: item.type }, { label: "Aksiyon", value: item.action },
    { label: "Entity türü", value: item.entity_type }, { label: "Entity ID", value: item.entity_id }, { label: "Okundu", value: item.is_read ? "Evet" : "Hayır" },
    { label: "Oluşturuldu", value: formatDate(item.created_at) }, { label: "Güncellendi", value: formatDate(item.updated_at) }, { label: "Gönderildi", value: formatDate(item.sent_at) }, { label: "Okunduğu tarih", value: formatDate(item.read_at) },
  ]} /></div>;
}

function CampaignDetail({ item }: { item: NotificationCampaign }) {
  const dataEntries = Object.entries(item.data);
  return <div className={styles.drawerBody}><div className={styles.messagePreview}><Megaphone size={20} /><div><strong>{item.title}</strong><p>{item.body}</p></div></div><Summary value={item} /><DetailRows rows={[
    { label: "Kampanya ID", value: <code>{item.campaign_id}</code> }, { label: "Kitle", value: item.audience.type === "all" ? "Tüm kullanıcılar" : "Seçili kullanıcılar" },
    { label: "İstenen kullanıcı", value: item.requested_users }, { label: "Hedef cihaz", value: item.targeted_devices }, { label: "Oluşturuldu", value: formatDate(item.created_at) }, { label: "Güncellendi", value: formatDate(item.updated_at) },
  ]} />{item.audience.user_ids.length > 0 && <section className={styles.dataBlock}><strong>Kullanıcı ID’leri</strong><div>{item.audience.user_ids.map((id) => <code key={id}>{id}</code>)}</div></section>}{dataEntries.length > 0 && <section className={styles.dataBlock}><strong>Ek data</strong><div>{dataEntries.map(([key, value]) => <code key={key}>{key}={value}</code>)}</div></section>}</div>;
}
